import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { getAppLocale } from './window.utils';

dayjs.extend(utc);
dayjs.extend(timezone);

export const isUtcDate = (date: string): boolean => /Z$|[+-]\d{2}:\d{2}$/.test(date);

const formatDateIfValid = (date: string, format: string, timezone?: string) => {
    const dayjsDate = isUtcDate(date) ? dayjs.utc(date).tz(resolveTimezone(timezone)) : dayjs(date);
    if (dayjsDate.isValid()) {
        return dayjsDate.format(format);
    }
    return '';
};

export const formatDateTime = (date: string, dateTimeFormat: string, timezone?: string) =>
    formatDateIfValid(date, dateTimeFormat, timezone);
export const formatDate = (date: string, dateFormat: string, timezone?: string) =>
    formatDateIfValid(date, dateFormat, timezone);
export const formatTime = (date: string, timeFormat: string, timezone?: string) =>
    formatDateIfValid(date, timeFormat, timezone);
export const formatDateWithWeekday = (dateString: string, dateFormat: string, timezone?: string) => {
    const formattedDate = formatDateIfValid(dateString, dateFormat, timezone);
    const resolvedTimezone = resolveTimezone(timezone);
    const weekday = new Intl.DateTimeFormat(getAppLocale(), {
        weekday: 'long',
        timeZone: resolvedTimezone,
    }).format(new Date(dateString));
    return `${weekday}, ${formattedDate}`;
};
export const utcToLocalDateTime = (utcDate: string, timezone: string): string =>
    dayjs.utc(utcDate).tz(resolveTimezone(timezone)).format('YYYY-MM-DDTHH:mm:ss');

const isValidTimezone = (timezone?: string): timezone is string => {
    if (!timezone?.trim()) {
        return false;
    }

    try {
        new Intl.DateTimeFormat('en-US', { timeZone: timezone.trim() });
        return true;
    } catch {
        return false;
    }
};

const getBrowserTimezone = (): string => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return isValidTimezone(browserTimezone) ? browserTimezone : 'UTC';
};

export const resolveTimezone = (
    timezone?: string,
    fallbackTimezones: ReadonlyArray<string | undefined> = []
): string => {
    if (isValidTimezone(timezone)) {
        return timezone.trim();
    }

    const fallbackTimezone = fallbackTimezones.find(isValidTimezone);
    if (fallbackTimezone) {
        return fallbackTimezone.trim();
    }

    return getBrowserTimezone();
};

export const dateWithTimezoneToUtc = (date: string | Date | dayjs.Dayjs, timezone?: string): dayjs.Dayjs => {
    const zonedDate = dayjs.tz(date, resolveTimezone(timezone));
    if (!zonedDate.isValid()) {
        throw new Error('Invalid date');
    }
    return zonedDate.utc();
};

export const toLocalDateTime = (date: string, timezone: string): string =>
    isUtcDate(date) ? utcToLocalDateTime(date, timezone) : date;

export const normalizeToUtc = (dateTime: string, timeZone: string): string =>
    isUtcDate(dateTime) ? dateTime : dateWithTimezoneToUtc(dateTime, timeZone).toISOString();

export const computeDayKey = (utcDateTime: string, timeZone: string): string =>
    dayjs.utc(utcDateTime).tz(resolveTimezone(timeZone)).format('YYYY-MM-DD');
